import { router } from "@inertiajs/react";
import { useState } from "react";
import Chart from "react-apexcharts";

import ButtonIcon from "@/components/Buttons/SimpleButton";
import FaqCardComponent from "@/components/Cards/FaqComponent";
import Dropdown from "@/components/Dropdown";
import Footer from "@/components/Footer";
import Typography from "@/components/Typography";
import { bigNumberFormatter } from "@/utils/bigNumberFormatter";
import { getYearOptions } from "@/utils/getYearOptions";

const yearsOptions: string[] = getYearOptions();

const chartMonths = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

const baseChartOptions: any = {
  chart: {
    animations: { enabled: false },
    background: "transparent",
    fontFamily: "Inter",
    stacked: true,
    toolbar: { show: false },
  },
  dataLabels: { enabled: false },
  grid: {
    padding: { right: 25, left: 15 },
    borderColor: "rgba(255, 255, 255, 0.2)",
  },
  plotOptions: {
    bar: {
      borderRadius: 0,
    },
  },
  tooltip: {
    y: {
      formatter: (value: number) => `${value.toLocaleString("pt-BR")} kWh`,
    },
  },
  legend: {
    show: false,
  },
  xaxis: {
    categories: chartMonths,
    labels: {
      style: { colors: "#fff" },
    },
  },
  yaxis: {
    labels: {
      style: { colors: "#fff" },
      formatter: (value: number) => bigNumberFormatter(value),
    },
    title: {
      text: "kWh",
      style: { color: "#fff", fontWeight: 700 },
    },
  },
};

const palette = ["#FBBF24", "#F97316", "#FB7185", "#38BDF8", "#A3E635", "#C4B5FD"];

const LegendItem = ({ color, label }: { color: string; label: string }) => (
  <div className="flex items-center gap-2 whitespace-nowrap">
    <span className="size-3 shrink-0 rounded-sm" style={{ backgroundColor: color }} />
    <span className="text-sm text-white">{label}</span>
  </div>
);

const EnergyGeneration = ({
  plantsSeries = [],
  consumptionSeries = [],
  compensatedSeries = [],
}: any) => {
  const today = new Date();
  const [filters, setFilters] = useState({
    year: new URLSearchParams(window.location.search).get("year")
      ? String(new URLSearchParams(window.location.search).get("year"))
      : String(today.getFullYear()),
  });

  const changeFilters = ({ year = filters.year }) => {
    setFilters({ year });
    router.reload({
      only: ["plantsSeries", "consumptionSeries", "compensatedSeries"],
      data: { year: year },
    });
  };

  const consumptionColor = "#479657";
  const compensatedColor = "#C8FDD3";

  const series = [
    ...plantsSeries.map((plant: any, index: number) => ({
      ...plant,
      group: "geracao",
      color: palette[index % palette.length],
    })),
    { name: "Consumo", data: consumptionSeries, group: "consumo", color: consumptionColor },
    { name: "Compensado", data: compensatedSeries, group: "compensado", color: compensatedColor },
  ];

  const options = baseChartOptions;

  return (
    <>
      <div className="mx-2 mt-2 h-full w-[99%] rounded-t-[20px] bg-primary500 px-32 py-16 max-md:px-4 max-md:py-8">
        <ButtonIcon
          onClick={() => router.get("/manager/power-plants")}
          variant="simple"
          label="Voltar para usinas"
          className="!w-fit"
        />
        <div className="mt-20 flex flex-col gap-[0.8rem]">
          <Typography variant="overline" color="white">
            GESTÃO
          </Typography>
          <Typography variant="h2" color="white">
            Geração de Energia
          </Typography>
        </div>
      </div>
      <div className="mx-2 -mt-4 h-full w-[99%] rounded-[20px] bg-primary400 px-32 pb-24 pt-12 max-md:px-4 max-md:py-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Typography variant="h4" color="white">
              Geração x Consumo
            </Typography>
            <div className="mt-1">
              <Typography variant="pSmall" color="white">
                Comparativo mensal entre a energia injetada por usina nas faturas de energia e o
                consumo total dos clientes.
              </Typography>
            </div>
          </div>
          <Dropdown
            options={yearsOptions}
            onSelect={(value) => {
              changeFilters({ year: value });
            }}
            defaultValue={filters.year}
          />
        </div>
        <div className="mt-8 w-full overflow-x-auto">
          <div className="min-w-[900px]">
            <div className="mb-4 flex items-end justify-between gap-4">
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                {plantsSeries.map((plant: any, index: number) => (
                  <LegendItem
                    key={plant.name}
                    color={palette[index % palette.length]}
                    label={plant.name}
                  />
                ))}
              </div>
              <div className="flex shrink-0 items-center gap-5">
                <LegendItem color={consumptionColor} label="Consumo" />
                <LegendItem color={compensatedColor} label="Compensado" />
              </div>
            </div>
            <Chart options={options} series={series} type="bar" width="100%" height="290px" />
          </div>
        </div>
      </div>
      <div className="flex size-full flex-col gap-28 px-40 py-32 max-md:px-4 max-md:py-8">
        <FaqCardComponent variant="questionSection" />
      </div>
      <Footer />
    </>
  );
};
export default EnergyGeneration;
